import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { discountToApr } from "@/lib/negotiation/terms";
import type { NegotiationThread } from "@/types/negotiations";

/** Discount-first creation input (back-compat: target_apr / max_acceptable_apr derived). */
export interface CreateThreadInput {
  supplier_email: string;
  supplier_name?: string | null;
  /** Denormalised PAR vendor identity — used for cooldown, roster, cross-nav. */
  vendor_name?: string | null;
  subject: string;
  /** Discount-first fields (preferred) */
  target_discount_pct?: number | null;
  target_days_early?: number | null;
  floor_discount_pct?: number | null;
  floor_apr?: number | null;
  /** Back-compat APR fields — inferred from discount fields if not supplied */
  target_apr?: number | null;
  max_acceptable_apr?: number | null;
  tone?: string | null;
  /** Additional recipient emails; seeds participant_emails. */
  recipient_emails?: string[];
  /**
   * Pass `true` to bypass the decline cooldown check.
   * The caller must surface a confirmation prompt before setting this.
   */
  overrideCooldown?: boolean;
}

/** Error thrown when the decline cooldown blocks a new thread creation. */
export class CooldownError extends Error {
  public readonly vendor_name: string;
  public readonly declined_at: string;
  public readonly cooldown_days: number;

  constructor(vendor_name: string, declined_at: string, cooldown_days: number) {
    const remaining = cooldown_days - daysSince(declined_at);
    super(
      `Cannot start a new negotiation with "${vendor_name}" — declined ${daysSince(declined_at).toFixed(0)} day(s) ago. Cooldown is ${cooldown_days} day(s) (${remaining.toFixed(0)} day(s) remaining). Pass overrideCooldown=true to bypass.`
    );
    this.name = "CooldownError";
    this.vendor_name = vendor_name;
    this.declined_at = declined_at;
    this.cooldown_days = cooldown_days;
  }
}

function daysSince(iso: string): number {
  return (Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24);
}

const DEFAULT_COOLDOWN_DAYS = 30;

async function fetchThreads(): Promise<NegotiationThread[]> {
  const { data, error } = await supabase
    .from("negotiation_threads")
    .select("*")
    .order("last_message_at", { ascending: false, nullsFirst: false });

  if (error) throw error;
  return (data ?? []) as NegotiationThread[];
}

/**
 * Fetch the cooldown setting for the current user.
 * Falls back to DEFAULT_COOLDOWN_DAYS if the settings row doesn't yet have the column.
 */
async function fetchCooldownDays(): Promise<number> {
  const { data, error } = await supabase
    .from("settings")
    .select("negotiation_decline_cooldown_days")
    .limit(1)
    .maybeSingle();

  if (error || !data) return DEFAULT_COOLDOWN_DAYS;
  return (
    ((data as Record<string, unknown>).negotiation_decline_cooldown_days as number | null) ??
    DEFAULT_COOLDOWN_DAYS
  );
}

/**
 * Find the most recent declined thread for a given vendor_name.
 * Returns null if none exists.
 */
async function fetchLatestDeclinedThread(vendor_name: string): Promise<NegotiationThread | null> {
  const { data, error } = await supabase
    .from("negotiation_threads")
    .select("*")
    .eq("vendor_name", vendor_name)
    .eq("status", "declined")
    .order("declined_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data as NegotiationThread | null;
}

export function useNegotiations() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const {
    data: threads = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["negotiation_threads"],
    queryFn: fetchThreads,
  });

  const createThreadMutation = useMutation({
    mutationFn: async (input: CreateThreadInput) => {
      // ── Derive APR fields from discount-first inputs ────────────────────
      const targetDiscountPct = input.target_discount_pct ?? null;
      const targetDaysEarly = input.target_days_early ?? null;
      const floorApr = input.floor_apr ?? null;

      // target_apr: prefer explicit, else derive from discount fields
      const targetApr =
        input.target_apr ??
        (targetDiscountPct != null && targetDaysEarly != null && targetDaysEarly > 0
          ? discountToApr(targetDiscountPct, targetDaysEarly)
          : null);

      // max_acceptable_apr maps to floor_apr (minimum yield the buyer accepts)
      const maxAcceptableApr = input.max_acceptable_apr ?? floorApr;

      // ── Decline cooldown check ──────────────────────────────────────────
      if (input.vendor_name && !input.overrideCooldown) {
        const [latestDeclined, cooldownDays] = await Promise.all([
          fetchLatestDeclinedThread(input.vendor_name),
          fetchCooldownDays(),
        ]);

        if (latestDeclined?.declined_at) {
          const elapsed = daysSince(latestDeclined.declined_at);
          if (elapsed < cooldownDays) {
            throw new CooldownError(input.vendor_name, latestDeclined.declined_at, cooldownDays);
          }
        }
      }

      // ── Build participant_emails from optional recipients ───────────────
      const participantEmails: string[] = input.recipient_emails
        ? [...new Set([input.supplier_email, ...input.recipient_emails])]
        : [input.supplier_email];

      // ── Insert the thread row ───────────────────────────────────────────
      const { data: threadData, error: threadError } = await supabase
        .from("negotiation_threads")
        .insert({
          supplier_email: input.supplier_email,
          supplier_name: input.supplier_name ?? null,
          vendor_name: input.vendor_name ?? null,
          subject: input.subject,
          target_apr: targetApr,
          target_days_early: targetDaysEarly,
          max_acceptable_apr: maxAcceptableApr,
          target_discount_pct: targetDiscountPct,
          floor_discount_pct: input.floor_discount_pct ?? null,
          floor_apr: floorApr,
          tone: input.tone ?? null,
          participant_emails: participantEmails,
          status: "active",
        })
        .select()
        .single();

      if (threadError) throw threadError;
      const thread = threadData as NegotiationThread;

      return { thread, message: undefined };
    },
    onSuccess: ({ thread }) => {
      queryClient.invalidateQueries({ queryKey: ["negotiation_threads"] });
      queryClient.invalidateQueries({ queryKey: ["negotiation_thread", thread.id] });
      toast({
        title: "Negotiation started",
        description: "Open the thread to generate the draft.",
      });
    },
    onError: (err) => {
      console.error("Failed to create negotiation thread:", err);
      const isCooldown = err instanceof CooldownError;
      toast({
        title: isCooldown ? "Cooldown active" : "Failed to start negotiation",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  return {
    threads,
    isLoading,
    error,
    refetch,
    createThread: createThreadMutation.mutateAsync,
    isCreating: createThreadMutation.isPending,
  };
}
